import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { HotToastService } from '@ngneat/hot-toast';
import { take } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Step } from 'src/app/core/dtos/step';
import { StepProgressService, PageStepEnum } from 'src/app/core/services/step-progress.service';
import { AddShoppingCartItemRequest, ProductDto, ShoppingSectionDto } from 'src/app/core/dtos/dto';
import { ProductService } from 'src/app/core/services/product.service';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { ProductModalComponent } from './product-modal/product-modal.component';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { BrandEnum, ProductShoppingFilterEnum, ProductShoppingSectionEnum, ProductTypeEnum, TestTypeEnum } from 'src/app/core/dtos/enum';
import { productSortTypeOrderMapping } from './product-filter-sort-mappings';
import { FilterSortData } from './filter-sort-data';
import { selectIsBusinessAccount } from 'src/app/state/my-account.selectors';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
})
export class ProductsComponent implements OnInit, OnDestroy {

  brandId: BrandEnum;
  steps: Step[];
  sections: ShoppingSectionDto[];
  filteredSections: ShoppingSectionDto[];
  productSubscription: Subscription;
  productModalSubscription: Subscription;
  checkWarningSubscription: Subscription;
  addProductSubscription: Subscription;
  businessAccountSubscription: Subscription;
  isBusinessAccount: boolean;
  isRegFlow: boolean;
  previousUrl: string;
  addItemWarningMesssage: string;
  productId: number;
  productIdToAdd: number;
  addShoppingCartItemRequest: AddShoppingCartItemRequest;
  studyMaterialUrl = "/registration/study-materials";
  orderSummaryUrl = "/registration/order-summary";
  filterSortData: FilterSortData;

  @ViewChild('addItemWarning') addItemWarning: ModalComponent;

  constructor(
    private configHelper: ConfigHelper,
    private stepProgressService: StepProgressService,
    private productService: ProductService,
    private shoppingCartService: ShoppingCartService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private loadingService: LoadingService,
    private toastService: HotToastService,
    private previousRouteService: PreviousRouteService,
    private store: Store,
    private modalService: NgbModal
  ) {
  }

  ngOnInit(): void {
    this.previousUrl = this.previousRouteService.getPreviousUrl();
    if (this.previousUrl === this.studyMaterialUrl || this.previousUrl === this.orderSummaryUrl) {
      this.isRegFlow = true;
      this.steps = this.stepProgressService.getSteps(PageStepEnum.StudyMaterials);
    }
    this.brandId = this.configHelper.getBrandId();
    this.checkBusinessAccount();
    this.getProducts();
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  public get TestTypeEnum() {
    return TestTypeEnum;
  }

  getProducts() {
    this.productSubscription = this.productService.getShoppingSections()
      .subscribe(x => {
        //remove featured products (section id = 1)
        this.sections = x.content.filter(sec => sec.id !== 1);
        this.sections.forEach(p => {
          p.products.forEach(item => {
            item.imageUrl = this.configHelper.getProductImageUrl(item.imageUrl);
          });
          //dafault sort
          p.products.sort(productSortTypeOrderMapping[0].sortRule);
        });
        this.activeRoute.queryParams.subscribe((params: { productId: string }) => {
          this.productId = Number(params.productId);
          if (this.productId) {
            this.loadModal();
          }
        });
      });
  }

  public get filteredProducts() {
    if (!this.filterSortData) {
      return this.sections;
    }
    else {
      return this.filteredSections;
    }

  }

  checkBusinessAccount() {
    this.businessAccountSubscription = this.store.select(selectIsBusinessAccount)
      .subscribe(
        result => {
          this.isBusinessAccount = result;
        }
      );
  }

  loadModal() {
    const products: ProductDto[] = [];
    this.sections.forEach(x => x.products.forEach(p => products.push(p)));
    const product = products.find(x => x.id === this.productId);
    if (product) {
      const modalRef = this.modalService.open(ProductModalComponent, { size: 'lg' });
      modalRef.componentInstance.product = product;
      modalRef.componentInstance.brandId = this.brandId;
      modalRef.componentInstance.isRegFlow = false;
      modalRef.componentInstance.isBusinessAccount = this.isBusinessAccount;
      modalRef.componentInstance.addItem.subscribe((i) => this.addItemToCart(i));
    } else {
      this.productModalSubscription = this.productService.getProductInfo(this.productId).subscribe(x => {
        const productDetails = x.content;
        if (productDetails && productDetails.isActive) {
          productDetails.imageUrl = this.configHelper.getProductImageUrl(productDetails.imageUrl);
          const modalRef = this.modalService.open(ProductModalComponent, { size: 'lg' });
          modalRef.componentInstance.product = productDetails;
          modalRef.componentInstance.brandId = this.brandId;
          modalRef.componentInstance.isRegFlow = false;
          modalRef.componentInstance.isBusinessAccount = this.isBusinessAccount;
          modalRef.componentInstance.addItem.subscribe((i) => this.addItemToCart(i));
        }
      });
    }
  }

  filterSections(selectedFilterValue: number, selectedSortValue: number) {
    const selectedSort = productSortTypeOrderMapping.find(v => v.value === selectedSortValue).sortRule;
    const sections = this.sections.map(a => ({ ...a }));
    this.loadingService.show();
    interval(250).pipe(take(1))
      .subscribe(() => {
        switch (selectedFilterValue) {
          case ProductShoppingFilterEnum.CELPIPGeneral:
            this.filteredSections = sections.filter(s =>
              (![ProductShoppingSectionEnum.CELPIP_GENERAL_LS_PAPERBACK, ProductShoppingSectionEnum.CELPIP_GENERAL_LS_PRACTICE_TESTS].includes(s.id))).filter(s =>
              (s.products = s.products.filter(
                p => p.testTypeIds.includes(selectedFilterValue)
              )));
            break;
          case ProductShoppingFilterEnum.CELPIPLS:
            this.filteredSections = sections.filter(s =>
              (![ProductShoppingSectionEnum.CELPIP_GENERAL_PRACTICE_TESTS, ProductShoppingSectionEnum.CELPIP_GENERAL_PAPERBACK].includes(s.id))).filter(s =>
              (s.products = s.products.filter(
                p => p.testTypeIds.includes(selectedFilterValue)
              )));
            break;
          case ProductShoppingFilterEnum.PrintMaterial:
            this.filteredSections = sections.filter(s =>
            (s.products = s.products.filter(
              p => p.productTypeId === ProductTypeEnum.PhysicalProduct
            )));
            break;
          case ProductShoppingFilterEnum.TeacherResource:
            this.filteredSections = sections.filter(s =>
              (s.id === ProductShoppingSectionEnum.CELPIP_TEACHER_RESOURCES));
            break;
          case ProductShoppingFilterEnum.TestTakerStudyMaterial:
            this.filteredSections = sections.filter(s =>
              (s.id !== ProductShoppingSectionEnum.CELPIP_TEACHER_RESOURCES));
            break;
        }
        this.filteredSections.forEach(p => {
          p.products.sort(selectedSort);
        });
        this.loadingService.hide();
      });
  }

  filterSortChangeHandler(filterSortData: FilterSortData) {
    this.filterSortData = filterSortData;
    this.filterSections(filterSortData.filter, filterSortData.sort);
  }

  addItemToCart(product: ProductDto) {
    this.productIdToAdd = product.id;
    this.addShoppingCartItemRequest = {
      productId: product.id
    };
    this.checkWarningSubscription = this.shoppingCartService.checkForWarnings(product.id)
      .subscribe(
        (result) => {
          if (result.content === "") {
            this.finalizeAdd();
          }
          else {
            if (this.modalService.hasOpenModals) {
              this.modalService.dismissAll();
            }
            this.addItemWarningMesssage = result.content;
            this.addItemWarning.open();
          }
        }
        ,
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            if (errorMessage?.reasons) {
              errorMessage.reasons.forEach(reasons => {
                this.toastService.warning(reasons.errorMessage);
              });
            }
          }
        }
      );
  }

  finalizeAdd() {
    this.addProductSubscription = this.shoppingCartService.addProduct(this.addShoppingCartItemRequest)
      .subscribe(
        result => {
          this.shoppingCartService.setShoppingCart(result);
          if (this.isRegFlow) {
            this.router.navigate(['/registration/order-summary']);
          }
          else {
            this.router.navigate(['/shopping-cart']);
          }
        },
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            if (errorMessage?.reasons) {
              errorMessage.reasons.forEach(reasons => {
                this.toastService.warning(reasons.errorMessage);
              });
            }
            else {
              this.toastService.warning('Unable to add item to cart');
            }
          }
        });
  }

  ngOnDestroy(): void {
    this.productSubscription?.unsubscribe();
    this.checkWarningSubscription?.unsubscribe();
    this.addProductSubscription?.unsubscribe();
    this.productModalSubscription?.unsubscribe();
    this.businessAccountSubscription?.unsubscribe();
  }

}
